/* eslint-disable complexity */
/* eslint-disable no-underscore-dangle */
import '@react-native-firebase/firestore'; // Required: registers firestore with app for modular API
import '@react-native-firebase/remote-config'; // Required: registers remoteConfig with app for modular API

import RNFirebase from '@react-native-firebase/app';
import RNFirebaseAuth from '@react-native-firebase/auth';
import RNFirebaseMessaging from '@react-native-firebase/messaging';
import { MD5 } from 'crypto-js';
import {
    useEffect,
    useState
} from 'react';
import { Platform } from 'react-native';

import { getNetworkStatus } from '../hooks/useNetworkStatus';
import {
    debouncer,
    isEmpty
} from '../utils';
import { storage } from './localstorage';
import tracking from './tracking';

// Modular API for Remote Config and Firestore (native only) - avoids deprecation warnings in v22+
let getAppModular;
let getRemoteConfigModular;
let setDefaultsModular;
let fetchRemoteConfigModular;
let fetchAndActivateModular;
let getAllModular;
let getFirestoreModular;
let fsCollection;
let fsDoc;
let fsQuery;
let fsWhere;
let fsOrderBy;
let fsLimit;
let fsGetDocs;
let fsGetDoc;
let fsOnSnapshot;
let fsSetDoc;
let fsInitializeFirestore;
let fsTimestamp;
let getAuthModular;
let getIdTokenModular;
let onAuthStateChangedModular;
let signInAnonymouslyModular;
let signOutModular;
let signInWithPhoneNumberModular;
let signInWithCredentialModular;
let signInWithCustomTokenModular;
let getMessagingModular;
let getTokenModular;
let isDeviceRegisteredForRemoteMessagesModular;
if (Platform.OS !== 'web') {
    const appModular = require('@react-native-firebase/app');
    const remoteConfigModular = require('@react-native-firebase/remote-config/lib/modular');
    const firestoreModular = require('@react-native-firebase/firestore/lib/modular');
    const authModular = require('@react-native-firebase/auth/lib/modular');
    const { getMessaging, getToken, isDeviceRegisteredForRemoteMessages } = require('@react-native-firebase/messaging');
    getAppModular = appModular.getApp;
    getRemoteConfigModular = remoteConfigModular.getRemoteConfig;
    setDefaultsModular = remoteConfigModular.setDefaults;
    fetchRemoteConfigModular = remoteConfigModular.fetch;
    fetchAndActivateModular = remoteConfigModular.fetchAndActivate;
    getAllModular = remoteConfigModular.getAll;
    getFirestoreModular = firestoreModular.getFirestore;
    getAuthModular = authModular.getAuth;
    getIdTokenModular = authModular.getIdToken;
    onAuthStateChangedModular = authModular.onAuthStateChanged;
    signInAnonymouslyModular = authModular.signInAnonymously;
    signOutModular = authModular.signOut;
    signInWithPhoneNumberModular = authModular.signInWithPhoneNumber;
    signInWithCredentialModular = authModular.signInWithCredential;
    signInWithCustomTokenModular = authModular.signInWithCustomToken;
    fsCollection = firestoreModular.collection;
    fsDoc = firestoreModular.doc;
    fsQuery = firestoreModular.query;
    fsWhere = firestoreModular.where;
    fsOrderBy = firestoreModular.orderBy;
    fsLimit = firestoreModular.limit;
    fsGetDocs = firestoreModular.getDocs;
    fsGetDoc = firestoreModular.getDoc;
    fsOnSnapshot = firestoreModular.onSnapshot;
    fsSetDoc = firestoreModular.setDoc;
    fsInitializeFirestore = firestoreModular.initializeFirestore;
    fsTimestamp = firestoreModular.Timestamp;
    getMessagingModular = getMessaging;
    getTokenModular = getToken;
    isDeviceRegisteredForRemoteMessagesModular = isDeviceRegisteredForRemoteMessages;
    fsInitializeFirestore(getAppModular(), { persistence: false }).catch(() => {});
} else {
    const { getIdToken } = require('firebase/auth');
    getIdTokenModular = getIdToken;
}

export const collections = [ 'clinics' ];
export const staticCollections = [ 'settings' ];
export const pureCollections = [ 'notifications' ]; // 'settings' // 'physicians_feedback_sum', 'physicians_timetable_sum', 'physicians_prices_sum' // 'clinics', 'notifications', 'settings'
const collectionsWithDumps = [ 'physicians', 'clinics' ];
export const splitCollectionsToUpdate = [];
export const splitCollectionsSums = [];
export const splitCollectionsToSubscribe = [];
export const dinymicSplitCollectionsToSubscribe = [];

const collectionsUnsubscriptions = {};
const splitCollectionsUnsubscriptions = {};
const profileCollectionsUnsubscriptions = {};

// Firestore settings (persistence: false) set via initializeFirestore in modular block above

const _appVerifier = null;
const getRecaptchaVerifier = () =>
    _appVerifier ||
    (Platform.OS === 'web'
        ? RNFirebase.app()
            .RecaptchaVerifier()
        : new RNFirebaseAuth.RecaptchaVerifier('recaptcha-container', { size: 'invisible' }));
let recaptchaVerifier;

const snapshotDataObject = (snap) => {
    const data = {};
    snap?.forEach((doc) => {
        const {
            deleted,
            _id,
            ...fields
        } = doc.data();
        if (!deleted) {
            const id = doc.id || _id;
            data[id] = {
                ...fields,
                _id: id,
                id,
                path: doc.ref.path,
            };
        }
    });
    return data;
};
const snapshotToData = (snapshot) => {
    const result = [];
    snapshot?.forEach((doc) => {
        const e = doc.data();
        const id = e._id || doc.id;
        result.push({
            ...e,
            id,
        });
    });
    return result;
};
const querySnapshotToData = (querySnapshot) => {
    const data = {};
    if (!querySnapshot.size) {
        return;
    }
    const { docs } = querySnapshot;
    for (let i = 0; i < docs.length; i++) {
        const docData = docs[i].data();
        const id = docs[i].id || docData._id;
        data[id] = {
            ...docData,
            _id: id,
            id,
            path: docs[i].ref.path,
        };
    }
    // eslint-disable-next-line consistent-return
    return data;
};

// noinspection JSUnusedGlobalSymbols,JSUnusedLocalSymbols
const firebaseService = {
    get app() {
        return RNFirebase.app();
    },
    get auth() {
        if (Platform.OS === 'web') {
            return this.app.auth();
        }
        return getAuthModular(getAppModular());
    },
    get cfg() {
        if (Platform.OS === 'web') {
            return this.app.remoteConfig();
        }
        return getRemoteConfigModular(getAppModular());
    },

    get firestore() {
        if (this._firestore) {
            return this._firestore;
        }
        this._firestore = Platform.OS === 'web' ? this.app.firestore() : getFirestoreModular();
        return this._firestore;
    },
    /** ****************************
     * FireStore
     ***************************** */
    snapshotToData,
    snapshotDataObject,

    userStatsRef() {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            return db.collection('userStats').doc(this.auth.currentUser.uid);
        }
        return fsDoc(fsCollection(db, 'userStats'), this.auth.currentUser.uid);
    },

    subscribeOnDocChanges(coll, id, cb, hot) {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            const docRef = db.collection(coll).doc(id);
            if (hot) docRef.get().then(cb);
            return docRef.onSnapshot(cb);
        }
        const docRef = fsDoc(fsCollection(db, coll), id);
        if (hot) fsGetDoc(docRef).then(cb);
        return fsOnSnapshot(docRef, cb);
    },

    collection(coll) {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            return db.collection(coll);
        }
        const pathSegments = coll.split('/');
        const collRef = pathSegments.length === 1
            ? fsCollection(db, coll)
            : pathSegments.reduce((ref, segment, i) => (
                i % 2 === 0 ? fsCollection(ref || db, segment) : fsDoc(ref, segment)
            ), null);
        return {
            onSnapshot: (cb) => fsOnSnapshot(collRef, cb),
        };
    },

    subscribeOnCollection(coll, cb) {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            db.collection(coll).get().then(cb);
            return db.collection(coll).onSnapshot(cb);
        }
        const collRef = fsCollection(db, coll);
        fsGetDocs(collRef).then(cb);
        return fsOnSnapshot(collRef, cb);
    },

    subscribeOnCollectionChanges(coll, cb) {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            profileCollectionsUnsubscriptions[coll] = db.collection(coll).onSnapshot(cb);
            return;
        }
        profileCollectionsUnsubscriptions[coll] = fsOnSnapshot(fsCollection(db, coll), cb);
    },

    getCollectionsKeys() {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            return db.collection('refData').doc('collectionsKeys');
        }
        const docRef = fsDoc(fsCollection(db, 'refData'), 'collectionsKeys');
        return { get: () => fsGetDoc(docRef) };
    },

    unsubscribeFromProfileCollectionsChanges() {
        Object.keys(profileCollectionsUnsubscriptions)
            .forEach((coll) => {
                const unsubscribe = profileCollectionsUnsubscriptions[coll];
                if (unsubscribe) {
                    unsubscribe();
                    delete profileCollectionsUnsubscriptions[coll];
                }
            });
    },
    unsubscribeFromSplitCollectionsChanges() {
        Object.keys(splitCollectionsUnsubscriptions)
            .forEach((coll) => {
                const unsubscribe = splitCollectionsUnsubscriptions[coll];
                if (unsubscribe) {
                    unsubscribe();
                    delete splitCollectionsUnsubscriptions[coll];
                }
            });
    },

    resetSplitCollections() {
        splitCollectionsToSubscribe.splice(0, splitCollectionsToSubscribe.length);
        splitCollectionsToUpdate.splice(0, splitCollectionsToUpdate.length);
        dinymicSplitCollectionsToSubscribe.splice(0, dinymicSplitCollectionsToSubscribe.length);
    },

    unsubscribeFromCollectionsChanges() {
        collections.forEach((coll) => {
            const unsubscribe = collectionsUnsubscriptions[coll];
            if (unsubscribe) {
                unsubscribe();
                delete collectionsUnsubscriptions[coll];
            }
        });
        pureCollections.forEach((coll) => {
            const unsubscribe = collectionsUnsubscriptions[coll];
            if (unsubscribe) {
                unsubscribe();
                delete collectionsUnsubscriptions[coll];
            }
        });
        staticCollections.forEach((coll) => {
            const unsubscribe = collectionsUnsubscriptions[coll];
            if (unsubscribe) {
                unsubscribe();
                delete collectionsUnsubscriptions[coll];
            }
        });
    },

    collectionsData: {},
    async onlySubscribeOnCollection(coll) {
        const debounce = debouncer(20000);
        const db = this.firestore;
        const {
            lastMax = -1,
            data = {},
        } = await storage.getObject(`${coll}`);
        const subscribeCollection = (ts) => {
            const unsubscribe = collectionsUnsubscriptions[coll];
            if (unsubscribe) {
                unsubscribe();
            }

            const onSnapshotCb = (snapshot) => {
                if (!snapshot || !snapshot.size) {
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    return;
                }
                let max = 0;
                for (let i = 0; i < docsChanges.length; i++) {
                    const docChange = docsChanges[i];
                    const { doc } = docChange;
                    const docData = doc.data();
                    const id = doc.id || docData._id;
                    if (!docData.deleted && docChange.type !== 'removed') {
                        Object.assign(docData, {
                            _id: id,
                            id,
                            path: doc.ref.path,
                        });
                        if (data[id]) {
                            Object.assign(data[id], docData);
                        } else {
                            data[id] = docData;
                        }
                    } else {
                        delete data[id];
                    }
                    max = Math.max(docData._lastModifiedAt || 0, max);
                }
                if (ts < max) {
                    debounce(() =>
                        storage.set(`${coll}`, {
                            data,
                            lastMax: max,
                        }),
                    );
                }
            };
            if (Platform.OS === 'web') {
                collectionsUnsubscriptions[coll] = db.collection(coll)
                    .where('_lastModifiedAt', '>', ts)
                    .onSnapshot(onSnapshotCb);
            } else {
                const q = fsQuery(fsCollection(db, coll), fsWhere('_lastModifiedAt', '>', ts));
                collectionsUnsubscriptions[coll] = fsOnSnapshot(q, onSnapshotCb);
            }
        };
        subscribeCollection(lastMax);
    },
    getItemsFromCollection(coll, arr = []) {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            return db.collection(coll).where('_id', 'in', arr).get()
                .then((querySnapshot) => querySnapshotToData(querySnapshot));
        }
        const q = fsQuery(fsCollection(db, coll), fsWhere('_id', 'in', arr));
        return fsGetDocs(q).then((querySnapshot) => querySnapshotToData(querySnapshot));
    },
    getActualCollection(coll, cb, newSubscribe, onAllSync) {
        const db = this.firestore;
        tracking.logEvent(`sync_all_${coll}`);
        onAllSync && onAllSync(coll, true);
        const runQuery = () => {
            if (Platform.OS === 'web') {
                return db.collection(coll).where('deleted', '!=', true).get();
            }
            const q = fsQuery(fsCollection(db, coll), fsWhere('deleted', '!=', true));
            return fsGetDocs(q);
        };
        const runOrderByQuery = () => {
            if (Platform.OS === 'web') {
                return db.collection(coll).orderBy('_lastModifiedAt', 'desc').limit(1).get();
            }
            const q = fsQuery(
                fsCollection(db, coll),
                fsOrderBy('_lastModifiedAt', 'desc'),
                fsLimit(1),
            );
            return fsGetDocs(q);
        };
        runQuery().then(async (querySnapshot) => {
            const debounce = debouncer(20000);
            const data = {};
            if (!querySnapshot.size) {
                return;
            }
            const actualLastModifiedAtSnapsh = await runOrderByQuery();

            const actualLastModifiedAt = actualLastModifiedAtSnapsh?.docs
                ? actualLastModifiedAtSnapsh.docs.length &&
                    actualLastModifiedAtSnapsh?.docs[0]?.data()?._lastModifiedAt
                : -1;

            const { docs } = querySnapshot;
            for (let i = 0; i < docs.length; i++) {
                const docData = docs[i].data();
                const id = docs[i].id || docData._id;
                data[id] = {
                    ...docData,
                    _id: id,
                    id,
                    path: docs[i].ref.path,
                };
            }
            cb(coll, data);
            this.collectionsData[coll] = {
                data,
                lastMax: actualLastModifiedAt,
                isSaved: false,
            };
            debounce(() =>
                storage.set(`${coll}`, {
                    data,
                    lastMax: actualLastModifiedAt,
                }),
            );
            onAllSync && onAllSync(coll, false);
            newSubscribe(actualLastModifiedAt);
        });
    },

    syncIsChecked: {},

    async updateCollection(coll, cb, onStopFetching, onAllSync, onFetching) {
        const debounce = debouncer(20000);
        onFetching && onFetching(true);
        if (!this.collectionsData[coll]?.data) {
            const result = await storage.getObject(`${coll}`);
            this.collectionsData[coll] = {
                data: result.data || {},
                lastMax: result.lastMax || -1,
            };
            if (!isEmpty(this.collectionsData[coll]?.data)) {
                cb(coll, this.collectionsData[coll]?.data);
            }
        }
        const db = this.firestore;
        const isActual = !collectionsWithDumps.some((collName) => collName === coll);
        const subscribeCollection = (ts) => {
            const runQuery = () => {
                if (Platform.OS === 'web') {
                    return db.collection(coll).where('_lastModifiedAt', '>', ts).get();
                }
                const q = fsQuery(fsCollection(db, coll), fsWhere('_lastModifiedAt', '>', ts));
                return fsGetDocs(q);
            };
            runQuery().then(async (snapshot) => {
                onStopFetching && onStopFetching(coll);
                const data = this.collectionsData[coll]?.data;
                const getHash = () =>
                    Object.keys(data)
                        .filter((k) => k !== 'undefined')
                        .sort()
                        .join('');
                if (!snapshot || !snapshot.size) {
                    if (!isActual && !this.syncIsChecked[coll]) {
                        try {
                            this.syncIsChecked[coll] = true;
                            const collectionsKeysSnapshot = await this.getCollectionsKeys()
                                .get();
                            const collectionsKeysSnapshotData = collectionsKeysSnapshot.data() || {};
                            // eslint-disable-next-line max-depth
                            if (
                                collectionsKeysSnapshotData[coll] &&
                                    collectionsKeysSnapshotData[coll] !== getHash()
                            ) {
                                this.getActualCollection(coll, cb, subscribeCollection, onAllSync);
                                onFetching && onFetching(false);
                                return;
                            }
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.log('error: ', e);
                        }
                    }
                    onFetching && onFetching(false);
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    onFetching && onFetching(false);
                    return;
                }
                let max = 0;
                for (let i = 0; i < docsChanges.length; i++) {
                    const docChange = docsChanges[i];
                    const { doc } = docChange;
                    const docData = doc.data();
                    const id = doc.id || docData._id;
                    if (!docData.deleted && docChange.type !== 'removed') {
                        Object.assign(docData, {
                            _id: id,
                            id,
                            path: doc.ref.path,
                        });
                        if (data[id]) {
                            Object.assign(data[id], docData);
                        } else {
                            data[id] = docData;
                        }
                    } else {
                        delete data[id];
                    }
                    max = Math.max(docData._lastModifiedAt || 0, max);
                }
                if (!isActual && !this.syncIsChecked[coll]) {
                    try {
                        this.syncIsChecked[coll] = true;
                        const collectionsKeysSnapshot = await this.getCollectionsKeys()
                            .get();
                        const collectionsKeysSnapshotData = collectionsKeysSnapshot.data() || {};
                        if (collectionsKeysSnapshotData[coll] && collectionsKeysSnapshotData[coll] !== getHash()) {
                            this.getActualCollection(coll, cb, subscribeCollection, onAllSync);
                            onFetching && onFetching(false); // !
                            return;
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.log('error: ', e);
                    }
                }
                onFetching && onFetching(false);
                if (ts < max) {
                    this.collectionsData[coll] = {
                        data,
                        lastMax: max,
                        isSaved: false,
                    };
                    cb(coll, data);
                    debounce(() => {
                        (async () => {
                            await storage.set(`${coll}`, {
                                data,
                                lastMax: max,
                            });
                            // onFetching && onFetching(false);
                        })();
                    }); // .then(() => console.log(`${coll}: `, Date.now() - ddd2, 'write_storage'))); // .then(() => onLog(coll, Date.now() - ddd2, 'write_storage')));
                } else {
                    // onFetching && onFetching(false);
                }
            });
        };
        subscribeCollection(this.collectionsData[coll]?.lastMax);
    },

    async setAndUpdateCollection(coll, cb, onStopFetching, onAllSync, onLog) {
        const debounce = debouncer(20000);
        if (!this.collectionsData[coll]?.data) {
            const result = await storage.getObject(`${coll}`);
            this.collectionsData[coll] = {
                data: result.data || {},
                lastMax: result.lastMax || -1,
            };
            if (!isEmpty(this.collectionsData[coll]?.data)) {
                cb(coll, this.collectionsData[coll]?.data);
            }
        }

        const db = this.firestore;
        const isActual = !collectionsWithDumps.some((collName) => collName === coll);
        const subscribeCollection = (ts) => {
            const unsubscribe = collectionsUnsubscriptions[coll];
            if (unsubscribe) {
                unsubscribe();
            }

            const onSnapshotCb = async (snapshot) => {
                onStopFetching && onStopFetching(coll);
                const data = this.collectionsData[coll]?.data;
                const getHash = () =>
                    Object.keys(data)
                        .filter((k) => k !== 'undefined')
                        .sort()
                        .join('');
                if (!snapshot || !snapshot.size) {
                    if (!isActual && !this.syncIsChecked[coll]) {
                        try {
                            this.syncIsChecked[coll] = true;
                            const collectionsKeysSnapshot = await this.getCollectionsKeys()
                                .get();
                            const collectionsKeysSnapshotData = collectionsKeysSnapshot.data() || {};
                            // eslint-disable-next-line max-depth
                            if (
                                collectionsKeysSnapshotData[coll] &&
                                    collectionsKeysSnapshotData[coll] !== getHash()
                            ) {
                                this.getActualCollection(coll, cb, subscribeCollection, onAllSync);
                                return;
                            }
                        } catch (e) {
                            // eslint-disable-next-line no-console
                            console.log('error: ', e);
                        }
                    }
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    return;
                }
                let max = 0;
                for (let i = 0; i < docsChanges.length; i++) {
                    const docChange = docsChanges[i];
                    const { doc } = docChange;
                    const docData = doc.data();
                    const id = doc.id || docData._id;
                    if (!docData.deleted && docChange.type !== 'removed') {
                        Object.assign(docData, {
                            _id: id,
                            id,
                            path: doc.ref.path,
                        });
                        if (data[id]) {
                            Object.assign(data[id], docData);
                        } else {
                            data[id] = docData;
                        }
                    } else {
                        delete data[id];
                    }
                    max = Math.max(docData._lastModifiedAt || 0, max);
                }
                if (!isActual && !this.syncIsChecked[coll]) {
                    try {
                        this.syncIsChecked[coll] = true;
                        const collectionsKeysSnapshot = await this.getCollectionsKeys()
                            .get();
                        const collectionsKeysSnapshotData = collectionsKeysSnapshot.data() || {};
                        if (collectionsKeysSnapshotData[coll] && collectionsKeysSnapshotData[coll] !== getHash()) {
                            this.getActualCollection(coll, cb, subscribeCollection, onAllSync);
                            return;
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.log('error: ', e);
                    }
                }
                if (ts < max) {
                    this.collectionsData[coll] = {
                        data,
                        lastMax: max,
                        isSaved: false,
                    };
                    cb(coll, data);
                    // if (coll !== 'physicians') {
                    debounce(() =>
                        storage.set(`${coll}`, {
                            data,
                            lastMax: max,
                        }),
                    );
                }
            };
            if (Platform.OS === 'web') {
                collectionsUnsubscriptions[coll] = db.collection(coll)
                    .where('_lastModifiedAt', '>', ts)
                    .onSnapshot(onSnapshotCb);
            } else {
                const q = fsQuery(fsCollection(db, coll), fsWhere('_lastModifiedAt', '>', ts));
                collectionsUnsubscriptions[coll] = fsOnSnapshot(q, onSnapshotCb);
            }
        };
        subscribeCollection(this.collectionsData[coll]?.lastMax);
    },
    updatedDinymicCollection(coll, cb, onStopFetching, onLog) {
        const db = this.firestore;
        const debounce = debouncer(500);
        const lastMax = this.collectionsData[coll]?.lastMax || -1;
        const data = this.collectionsData[coll]?.data || {};
        const subscribeCollection = (ts) => {
            const unsubscribe = collectionsUnsubscriptions[coll];
            if (unsubscribe) {
                unsubscribe();
            }

            const runQuery = () => {
                if (Platform.OS === 'web') {
                    return db.collection(coll).where('_lastModifiedAt', '>', ts).get();
                }
                const q = fsQuery(fsCollection(db, coll), fsWhere('_lastModifiedAt', '>', ts));
                return fsGetDocs(q);
            };
            collectionsUnsubscriptions[coll] = runQuery().then((snapshot) => {
                onStopFetching && onStopFetching(coll);
                if (!snapshot || !snapshot.size) {
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    return;
                }
                let max = 0;
                for (let i = 0; i < docsChanges.length; i++) {
                    const docChange = docsChanges[i];
                    const { doc } = docChange;
                    const docData = doc.data();
                    const id = doc.id || docData._id;
                    if (docChange.type !== 'removed') {
                        if (data[id]) {
                            Object.assign(data[id], {
                                ...docData,
                                _id: id,
                                id,
                                path: doc.ref.path,
                            });
                        } else {
                            data[id] = docData;
                        }
                    } else {
                        delete data[id];
                    }
                    max = Math.max(docData._lastModifiedAt || 0, max);
                }
                debounce(() => cb(coll, data));
                this.collectionsData[coll] = {
                    data,
                    lastMax: max,
                };
            });
        };
        subscribeCollection(lastMax);
    },

    pureSubscribeOnCollection(coll, cb, onStopFetching, onLog) {
        const db = this.firestore;
        const debounce = debouncer(500);
        const lastMax = this.collectionsData[coll]?.lastMax || -1;
        const data = this.collectionsData[coll]?.data || {};
        const subscribeCollection = (ts) => {
            const unsubscribe = collectionsUnsubscriptions[coll];
            if (unsubscribe) {
                unsubscribe();
            }

            const onSnapshotCb = (snapshot) => {
                onStopFetching && onStopFetching(coll);
                if (!snapshot || !snapshot.size) {
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    return;
                }
                let max = 0;
                for (let i = 0; i < docsChanges.length; i++) {
                    const docChange = docsChanges[i];
                    const { doc } = docChange;
                    const docData = doc.data();
                    const id = doc.id || docData._id;
                    if (docChange.type !== 'removed') {
                        if (data[id]) {
                            Object.assign(data[id], {
                                ...docData,
                                _id: id,
                                id,
                                path: doc.ref.path,
                            });
                        } else {
                            data[id] = docData;
                        }
                    } else {
                        delete data[id];
                    }
                    max = Math.max(docData._lastModifiedAt || 0, max);
                }
                debounce(() => cb(coll, data));
                this.collectionsData[coll] = {
                    data,
                    lastMax: max,
                };
            };
            if (Platform.OS === 'web') {
                collectionsUnsubscriptions[coll] = db.collection(coll)
                    .where('_lastModifiedAt', '>', ts)
                    .onSnapshot(onSnapshotCb);
            } else {
                const q = fsQuery(fsCollection(db, coll), fsWhere('_lastModifiedAt', '>', ts));
                collectionsUnsubscriptions[coll] = fsOnSnapshot(q, onSnapshotCb);
            }
        };
        subscribeCollection(lastMax);
    },
    subscribeOnCollectionsChanges(cb, onStop, onAllSync, onLog) {
        collections.forEach((coll) => {
            this.setAndUpdateCollection(coll, cb, onStop, onAllSync, onLog);
        });
        pureCollections.forEach((coll) => {
            this.pureSubscribeOnCollection(coll, cb, onStop, onLog);
        });
        staticCollections.forEach((coll) => {
            this.onlySubscribeOnCollection(coll, cb, onStop, onLog);
        });
    },
    updateSplitCollectionsChanges(cb) {
        splitCollectionsToUpdate.forEach((coll) => {
            const [ collShortName,
                location,
                spec ] = coll.split('.');
            this.getSplitCollection(collShortName, location, spec, cb); // coll, location, spec, cb
        });
        splitCollectionsToSubscribe.forEach((coll) => {
            const [ collShortName,
                location,
                spec ] = coll.split('.');
            this.subscribeOnSplitCollection(collShortName, location, spec, cb); // coll, location, spec, cb
        });
        dinymicSplitCollectionsToSubscribe.forEach((coll) => {
            const [ collShortName,
                location,
                spec ] = coll.split('.');
            this.subscribeOnDinymicSplitCollection(collShortName, location, spec, cb); // coll, location, spec, cb
        });
    },

    splitCollectionsData: {},

    async firstSetOfSplitCollection(collectionName, cb) {
        if (!this.splitCollectionsData[collectionName]?.data) {
            const result = await storage.getObject(`${collectionName}`);
            this.splitCollectionsData[collectionName] = {
                data: result.data || {},
                lastMax: result.lastMax || -1,
            };
            if (!isEmpty(this.splitCollectionsData[collectionName]?.data)) {
                cb(collectionName, this.splitCollectionsData[collectionName]?.data);
            }
        }
    },

    getMaxAndSetDataFromSnapshot(docsChanges, data) {
        let max = 0;
        for (let i = 0; i < docsChanges.length; i++) {
            const docChange = docsChanges[i];
            const { doc } = docChange;
            const docData = doc.data();
            const id = doc.id || docData._id;
            if (docChange.type !== 'removed') {
                if (data[id]) {
                    Object.assign(data[id], {
                        ...docData,
                        _id: id,
                        id,
                        path: doc.ref.path,
                    });
                } else {
                    data[id] = docData;
                }
            } else {
                delete data[id];
            }

            max = Math.max(docData._lastModifiedAt || 0, max);
        }
        return max;
    },
    getMaxAndSetSumDataFromSnapshot(docsChanges, data, keyName) {
        let max = 0;
        for (let i = 0; i < docsChanges.length; i++) {
            const docChange = docsChanges[i];
            const { doc } = docChange;
            const docData = doc.data();
            if (docChange.type !== 'removed') {
                if (data) {
                    Object.assign(data, { ...docData?.[keyName] });
                } else {
                    // eslint-disable-next-line no-param-reassign
                    data = docData?.[keyName];
                }
            }

            max = Math.max(docData._lastModifiedAt || 0, max);
        }
        return max;
    },
    async getSplitCollection(coll, location, spec, cb) {
        const debounce = debouncer(500);
        const collectionName = `${coll}.${location}.${spec}`;
        await this.firstSetOfSplitCollection(collectionName, cb);
        const lastMax = this.splitCollectionsData[collectionName]?.lastMax || -1;
        const data = this.splitCollectionsData[collectionName]?.data || {};
        if (!splitCollectionsToUpdate.some((c) => c === collectionName)) {
            splitCollectionsToUpdate.push(collectionName);
        }
        const db = this.firestore;
        const collPath = `${coll}/${location}/${spec}`;
        const runQuery = () => {
            if (Platform.OS === 'web') {
                return db.collection(collPath).where('_lastModifiedAt', '>', lastMax).get();
            }
            const docRef = fsDoc(fsCollection(db, coll), location);
            const collRef = fsCollection(docRef, spec);
            const q = fsQuery(collRef, fsWhere('_lastModifiedAt', '>', lastMax));
            return fsGetDocs(q);
        };
        runQuery().then((snapshot) => {
            if (!snapshot || !snapshot.size) {
                return;
            }
            const docsChanges = snapshot.docChanges();
            if (!docsChanges.length) {
                return;
            }
            const max = this.getMaxAndSetDataFromSnapshot(docsChanges, data);
            debounce(() => {
                storage.set(`${collectionName}`, {
                    data,
                    lastMax: max,
                });
                cb(collectionName, data);
            });
            this.splitCollectionsData[collectionName] = {
                data,
                lastMax: max,
            };
        });
    },

    // eslint-disable-next-line max-params
    async getSplitCollectionSum(coll, location, spec, keyName, cb, onUpdate) {
        const debounce = debouncer(500);
        const collectionName = `${coll}.${location}.${spec}`;
        if (splitCollectionsSums.some((sum) => sum === collectionName)) {
            return;
        }
        const data = {};
        splitCollectionsSums.push(collectionName);
        const db = this.firestore;
        const collPath = `${coll}/${location}/${spec}`;
        const runGetQuery = () => {
            if (Platform.OS === 'web') {
                return db.collection(collPath).get();
            }
            // const docRef = fsDoc(fsCollection(db, coll), location);
            const collRef = fsCollection(db, collPath);
            return fsGetDocs(collRef);
        };
        runGetQuery().then(
            (snapshot) => {
                if (!snapshot || !snapshot.size) {
                    onUpdate && onUpdate(collectionName, true);
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    onUpdate && onUpdate(collectionName, true);
                    return;
                }
                const max = this.getMaxAndSetSumDataFromSnapshot(docsChanges, data, keyName);
                debounce(() => {
                    cb(collectionName, data);
                });
                this.splitCollectionsData[collectionName] = { lastMax: max };
                onUpdate && onUpdate(collectionName, true);
            },
            () => {
                onUpdate && onUpdate(collectionName, true);
            },
        );
    },

    async subscribeOnSplitCollection(coll, location, spec, cb) {
        const db = this.firestore;
        const debounce = debouncer(1000);
        const collectionName = `${coll}.${location}.${spec}`;
        await this.firstSetOfSplitCollection(collectionName, cb);
        const subscribeCollection = (ts) => {
            if (!splitCollectionsToSubscribe.some((c) => c === collectionName)) {
                splitCollectionsToSubscribe.push(collectionName);
            }
            const unsubscribe = splitCollectionsUnsubscriptions[collectionName];
            if (unsubscribe) {
                unsubscribe();
            }
            const collPath = `${coll}/${location}/${spec}`;
            const onSnapshotCb = async (snapshot) => {
                const data = this.splitCollectionsData[collectionName]?.data || {};
                if (!snapshot || !snapshot.size) {
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    return;
                }
                const max = this.getMaxAndSetDataFromSnapshot(docsChanges, data);
                if (ts < max) {
                    this.splitCollectionsData[collectionName] = {
                        data,
                        lastMax: max,
                    };
                    cb(collectionName, data);
                    debounce(() =>
                        storage.set(`${collectionName}`, {
                            data,
                            lastMax: max,
                        }),
                    );
                }
            };
            if (Platform.OS === 'web') {
                splitCollectionsUnsubscriptions[collectionName] = db.collection(collPath)
                    .where('_lastModifiedAt', '>', ts)
                    .onSnapshot(onSnapshotCb);
            } else {
                // const docRef = fsDoc(fsCollection(db, coll), location);
                const collRef = fsCollection(db, collPath);
                const q = fsQuery(collRef, fsWhere('_lastModifiedAt', '>', ts));
                splitCollectionsUnsubscriptions[collectionName] = fsOnSnapshot(q, onSnapshotCb);
            }
        };
        subscribeCollection(this.splitCollectionsData[collectionName]?.lastMax);
    },
    async subscribeOnDinymicSplitCollection(coll, location, spec, cb, _lastMax) {
        const db = this.firestore;
        const collectionName = `${coll}.${location}.${spec}`;
        const collPath = `${coll}/${location}/${spec}`;
        const subscribeCollection = (ts) => {
            if (!dinymicSplitCollectionsToSubscribe.some((c) => c === collectionName)) {
                dinymicSplitCollectionsToSubscribe.push(collectionName);
            }
            const unsubscribe = splitCollectionsUnsubscriptions[collectionName];
            if (unsubscribe) {
                unsubscribe();
            }
            const onSnapshotCb = async (snapshot) => {
                const data = this.splitCollectionsData[collectionName]?.data || {};
                if (!snapshot || !snapshot.size) {
                    return;
                }
                const docsChanges = snapshot.docChanges();
                if (!docsChanges.length) {
                    return;
                }
                const max = this.getMaxAndSetDataFromSnapshot(docsChanges, data);
                if (ts < max) {
                    this.splitCollectionsData[collectionName] = {
                        data,
                        lastMax: max,
                    };
                    cb(collectionName, data);
                }
            };
            if (Platform.OS === 'web') {
                splitCollectionsUnsubscriptions[collectionName] = db.collection(collPath)
                    .where('_lastModifiedAt', '>', ts)
                    .onSnapshot(onSnapshotCb);
            } else {
                // const docRef = fsDoc(fsCollection(db, coll), location);
                const collRef = fsCollection(db, collPath);
                const q = fsQuery(collRef, fsWhere('_lastModifiedAt', '>', ts));
                splitCollectionsUnsubscriptions[collectionName] = fsOnSnapshot(q, onSnapshotCb);
            }
        };
        subscribeCollection(this.splitCollectionsData[collectionName]?.lastMax || _lastMax || -1);
    },

    getById(coll, id) {
        if (!id) return null;
        const db = this.firestore;
        if (Platform.OS === 'web') {
            return db.collection(coll).doc(id).get()
                .then((r) => r?.data())
                .catch(() => null);
        }
        const docRef = fsDoc(fsCollection(db, coll), id);
        return fsGetDoc(docRef).then((r) => r?.data()).catch(() => null);
    },

    onUserProfile(storeId, onUserProfileUpdate) {
        if (!storeId) return null;
        const db = this.firestore;
        let unsubscribe;
        const onSnapshotCb = (snap) => {
            if (!snap?.metadata?.hasPendingWrites) {
                unsubscribe && unsubscribe();
                onUserProfileUpdate(snap?.data());
            }
        };
        if (Platform.OS === 'web') {
            unsubscribe = db.collection('profile').doc(storeId).onSnapshot(onSnapshotCb);
        } else {
            const docRef = fsDoc(fsCollection(db, 'profile'), storeId);
            unsubscribe = fsOnSnapshot(docRef, onSnapshotCb);
        }
        return unsubscribe;
    },

    getUserProfile(storeId) {
        return this.getById('profile', storeId);
    },

    setUserData(colName, data, storeId) {
        const db = this.firestore;
        if (Platform.OS === 'web') {
            db.collection('profile').doc(storeId).set({ [colName]: data }, { merge: true });
            return;
        }
        const docRef = fsDoc(fsCollection(db, 'profile'), storeId);
        fsSetDoc(docRef, { [colName]: data }, { merge: true });
    },

    setUserStats(props) {
        const userStatsRef = this.userStatsRef();
        if (Platform.OS === 'web') {
            userStatsRef.set(props, { merge: true });
            return;
        }
        fsSetDoc(userStatsRef, props, { merge: true });
    },

    setVisitsStats({
        id,
        ...props
    }) {
        const userStatsRef = this.userStatsRef();
        const visitId = MD5(id).toString();
        if (Platform.OS === 'web') {
            userStatsRef.collection('visits').doc(visitId).set(props, { merge: true });
            return;
        }
        const visitsDocRef = fsDoc(fsCollection(userStatsRef, 'visits'), visitId);
        fsSetDoc(visitsDocRef, props, { merge: true });
    },

    setVisitStatusIfExists(id, status) {
        const userStatsRef = this.userStatsRef();
        const visitId = MD5(id).toString();
        if (Platform.OS === 'web') {
            const docRef = userStatsRef.collection('visits').doc(visitId);
            docRef.get().then((r) => {
                if (r?.data()) docRef.set({ status }, { merge: true });
            });
            return;
        }
        const visitsDocRef = fsDoc(fsCollection(userStatsRef, 'visits'), visitId);
        fsGetDoc(visitsDocRef).then((r) => {
            if (r?.data()) fsSetDoc(visitsDocRef, { status }, { merge: true });
        });
    },

    setAuthStats({
        id,
        event,
        value,
    }) {
        if (!this.isAuthenticated() || id) {
            const db = this.firestore;
            const data = {};
            const tsKey = Platform.OS === 'web' ? db.Timestamp() : fsTimestamp.now().seconds.toString();
            data[tsKey] =
                !value
                    ? { event }
                    : {
                        event,
                        value,
                    };
            const docId = (id || this.auth.currentUser.uid).toString();
            if (Platform.OS === 'web') {
                db.collection('authStats').doc(docId).set(data, { merge: true });
                return;
            }
            const docRef = fsDoc(fsCollection(db, 'authStats'), docId);
            fsSetDoc(docRef, data, { merge: true });
        }
    },

    /** ****************************
     * Configuration
     ***************************** */
    initRemoteConfig(defaults = {}) {
        const parseParametersToData = (parameters) => {
            const data = {};
            Object.entries(parameters)
                .forEach((el) => {
                    const [ key, entry ] = el;
                    const value = entry.asString();
                    data[key] = value === 'true' || value === 'false' ? JSON.parse(value) : value;
                });
            return data;
        };

        if (Platform.OS === 'web') {
            const { cfg } = this;
            cfg.setDefaults(defaults);
            return cfg
                .fetch(1)
                .then(() => cfg.fetchAndActivate())
                .then(() => parseParametersToData(cfg.getAll()))
                .catch((error) => {
                    // eslint-disable-next-line no-console
                    console.log(`Error processing firebase remote config`, error);
                    throw error;
                });
        }

        // Native: use modular API to avoid deprecation warnings (v22+)
        const app = getAppModular();
        const remoteConfig = getRemoteConfigModular(app);
        return setDefaultsModular(remoteConfig, defaults)
            .then(() => fetchRemoteConfigModular(remoteConfig, 1))
            .then(() => fetchAndActivateModular(remoteConfig))
            .then(() => parseParametersToData(getAllModular(remoteConfig)))
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.log(`Error processing firebase remote config`, error);
                throw error;
            });
    },
    /** ****************************
     * Authentication
     ***************************** */

    async addAuthStateListener(listenerFunc) {
        const auth = this.auth;
        const onAuthCb = async (result) => {
            const id = await storage.get('anonymousId');
            if (id && auth.currentUser) {
                if (auth.currentUser.uid === id) {
                    this.setAuthStats({ event: 'app reopened' });
                } else {
                    this.setAuthStats({
                        id,
                        event: 'auth state changed',
                        value: auth.currentUser.uid,
                    });
                }
            }
            if (result === null) {
                if (Platform.OS === 'web') {
                    await this.app.auth().signInAnonymously();
                } else {
                    await signInAnonymouslyModular(auth);
                }
                this.setAuthStats({ event: 'anonymous created' });
            }
            listenerFunc(result);
        };
        if (Platform.OS === 'web') {
            return auth.onAuthStateChanged(onAuthCb);
        }
        return onAuthStateChangedModular(auth, onAuthCb);
    },

    getToken() {
        const user = this.auth.currentUser;
        if (!user) return null;
        return getIdTokenModular(user);
    },

    getUserId() {
        return this.auth.currentUser?.uid || null;
    },

    isAuthenticated() {
        const currentUser = this.auth.currentUser;
        return Boolean(currentUser && !currentUser.isAnonymous);
    },

    async checkAuth() {
        const auth = this.auth;
        if (await this.isAuthenticated()) {
            return true;
        } else if (!auth.currentUser && (await getNetworkStatus()).isReachable) {
            if (Platform.OS === 'web') {
                await this.app.auth().signInAnonymously();
            } else {
                await signInAnonymouslyModular(auth);
            }
            this.setAuthStats({ event: 'anonymous created' });
        }
        return false;
    },

    signOut() {
        const auth = this.auth;
        if (Platform.OS === 'web') {
            return auth.signOut();
        }
        return signOutModular(auth);
    },

    removeAccount() {
        //
    },

    async signInWithPhoneNumber(phoneNumber, cb = (r) => r) {
        const isOnline = (await getNetworkStatus()).isReachable;
        if (!isOnline) {
            throw new Error(Object.R('error.network_connection'));
        }
        if (Platform.OS === 'web') {
            if (!recaptchaVerifier) {
                recaptchaVerifier = getRecaptchaVerifier();
            }
            return this.auth.signInWithPhoneNumber(phoneNumber, recaptchaVerifier).then(cb);
        }
        this.setAuthStats({
            event: 'sending to phone',
            value: phoneNumber,
        });
        const t = this;
        const auth = this.auth;
        const phoneSignIn = Platform.OS === 'web'
            ? auth.signInWithPhoneNumber(phoneNumber, getRecaptchaVerifier())
            : signInWithPhoneNumberModular(auth, phoneNumber, true);
        return phoneSignIn
            .then(async (confirmationResult) => {
                this.setAuthStats({
                    event: 'sms sent',
                    value: phoneNumber,
                });
                cb(confirmationResult);
            })
            .catch((error) => {
                t.setAuthStats({
                    event: `sms not sent`,
                    // value: `${phoneNumber}: ${error?.message()}`,
                    value: phoneNumber,
                });
                // t.setAuthStats('sms not sent', error);
                // eslint-disable-next-line no-console
                console.log('Sms not send', error);
                throw error;
            });
    },

    async signConfirmation(confirmationResult, code, onError) {
        this.setAuthStats({
            event: 'code entered',
            value: code,
        });

        const credential =
            Platform.OS === 'web'
                ? this.app.getCredential(confirmationResult.verificationId, code || ' ')
                : RNFirebaseAuth.PhoneAuthProvider.credential(confirmationResult.verificationId, code || ' ');
        try {
            const auth = this.auth;
            const result = Platform.OS === 'web'
                ? await auth.signInWithCredential(credential).catch((e) => {
                    this.setAuthStats({ event: 'wrong code' });
                    throw e;
                })
                : await signInWithCredentialModular(auth, credential).catch((e) => {
                    this.setAuthStats({ event: 'wrong code' });
                    throw e;
                });
            return result;
        } catch (error) {
            if (onError) {
                onError(error);
            }
            throw error;
        }
    },
    async signInWithToken(token, onError) {
        try {
            const auth = this.auth;
            return Platform.OS === 'web'
                ? await auth.signInWithCustomToken(token).catch((e) => { throw e; })
                : await signInWithCustomTokenModular(auth, token).catch((e) => { throw e; });
        } catch (e) {
            if (onError) {
                onError(e);
            }
            throw e;
        }
    },

    /** ****************************
     * Notifications
     ***************************** */

    onMessage(cb) {
        try {
            RNFirebaseMessaging()
                .onMessage(cb);
        } catch (e) {
            //
        }
    },
    async checkForRemoteM() {
        try {
            if (RNFirebaseMessaging().isDeviceRegisteredForRemoteMessages) {
                return;
            }
            await RNFirebaseMessaging()
                .registerDeviceForRemoteMessages();
        } catch (e) {
            //
        }
    },

    getFCMToken() {
        try {
            if (Platform.OS === 'web') return null;
            const messaging = getMessagingModular(getAppModular());
            if (Platform.OS === 'ios' && !isDeviceRegisteredForRemoteMessagesModular(messaging)) return null;
            return getTokenModular(messaging);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log('Error: ', e);
            return null;
        }
    },
    getAPNSToken() {
        return RNFirebaseMessaging()
            .getAPNSToken();
    },

    // addDeepLinkReceiveListener(listenerFunc) {

    //     return Platform.OS !== 'web' && RNFirebaseDynamicLinks().onLink(listenerFunc);

    // },

    // async getStartLink() {

    //     return Platform.OS !== 'web' && RNFirebaseDynamicLinks().getInitialLink();

    // },

    async checkPushNotificationsPermission() {
        if (Platform.OS === 'ios') {
            return RNFirebaseMessaging()
                ?.hasPermission();
        }
        return true;
    },

    async subscribeToPushNotifications() {
        if (Platform.OS === 'ios') {
            // const enabled = await RNFirebaseMessaging().hasPermission();
            // if (!enabled) {
            //     await RNFirebaseMessaging().requestPermission();
            // }
        } else if (Platform.OS === 'android') {
            // const channel = new RNFirebaseMessaging.Android.Channel(
            //     'reminder',
            //     'Reminder',
            //     RNFirebaseMessaging.Android.Importance.Max,
            // );
            // RNFirebaseMessaging().android.createChannel(channel);
        }
        this.subscribeToNotifications();
    },

    subscribeToNotifications() {
        // if (!RNFirebaseMessaging) { return; } // web mode
        // notifications.notification({ title: 'Test', message: 'Subscribed' });
        // RNFirebaseMessaging().onMessage(async (message) => {
        //     const headless = await RNFirebaseMessaging().getIsHeadless();
        //     console.log(`%c Message from cloud HEADLESS=${headless}`, 'color: #EE00FF', message);
        // });
        // notifications.customNotificationHandler = (notify) => {
        //     if (notify?.foreground) {
        //         notifications.cancelNotification(notify?.id);
        //         console.log('Clear');
        //     }
        //     console.log(`%c Message opened - push notify`, `color: #EE00FF`, notify);
        // };
        // RNFirebaseMessaging().onNotificationOpenedApp(async (message) => {
        //     const headless = await RNFirebaseMessaging().getIsHeadless();
        //     console.log(`%c Message opened  HEADLESS=${headless}`, `color: #EE00FF`, message);
        // });
        // RNFirebaseMessaging().setBackgroundMessageHandler(async (message) => {
        //     const headless = await RNFirebaseMessaging().getIsHeadless();
        //     console.log(`%c setBackgroundMessageHandler HEADLESS=${headless}`, `color: #EE00FF`, message);
        // });
        // RNFirebaseMessaging().getInitialNotification().then(this.handleNotificationOpen);
        // RNFirebaseMessaging().onNotificationOpened(this.handleNotificationOpen);
        //
        // return RNFirebaseMessaging().onNotification(async (notification) => {
        //     notification
        //         .setSound('default')
        //         .android.setPriority(RNFirebaseMessaging.Android.Priority.High)
        //         .android.setChannelId('reminder')
        //         .android.setVibrate(1000)
        //         .android.setSmallIcon('@drawable/ic_notification')
        //         .android.setColor('#5C85DD');
        //     await RNFirebaseMessaging().displayNotification(notification);
        // });
    },

    handleNotificationOpen(notification) {
        if (!notification) {
            return;
        }
        const {
            popupTitle,
            popupMessage,
            popupAction,
        } = notification.notification._data;
        if (popupMessage) {
            // eslint-disable-next-line global-require
            const { showNotificationInfoModal } = require('../combo');
            showNotificationInfoModal(popupTitle, popupMessage, popupAction);
        }
    },

    scheduleNotification(notificationId, date, title = 'Aibolit', text = '') {
        if (Platform.OS === 'web') {
            return;
        }
        const notification = new RNFirebaseMessaging.Notification()
            .setNotificationId(notificationId)
            .setTitle(title)
            .setBody(text)
            .android
            .setPriority(RNFirebaseMessaging.Android.Priority.High)
            .android
            .setChannelId('reminder')
            .android
            .setAutoCancel(false)
            .android
            .setSmallIcon('@drawable/ic_notification')
            .android
            .setColor('#5C85DD');

        RNFirebaseMessaging()
            .scheduleNotification(notification, { fireDate: date.getTime() });
    },

    cancelAllNotifications() {
        if (Platform.OS === 'web') {
            return;
        }
        RNFirebaseMessaging()
            .cancelAllNotifications();
    },
};

export const useFirebaseItem = (coll, id) => {
    const [ item, setItem ] = useState(null);

    useEffect(() => {
        (async () => {
            setItem(null);
            const info = await firebaseService.getById(coll, id);
            if (info) {
                setItem(info);
            }
        })();
    }, [ coll, id ]);

    return item;
};

export const getItemById = (coll, id) => firebaseService.getById(coll, id);

export const useLiveFirebaseItem = (coll, id) => {
    const [ item, setItem ] = useState(null);
    useEffect(
        () =>
            firebaseService.subscribeOnDocChanges(coll, id, (doc) =>
                setItem({
                    ...doc?.data(),
                    id: doc?.id,
                }),
            ),
        [ coll, id ],
    );
    return item;
};

export const useLiveFirebaseCollection = (coll, params = []) => {
    const [ data, setData ] = useState(null);
    useEffect(
        () => firebaseService.subscribeOnCollection(coll, (ss) => setData(snapshotDataObject(ss))),
        [ coll, ...params ],
    );
    return data;
};

export default firebaseService;
